import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Calculator, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import api, { formatApiError } from "../../lib/api";
import { getSchema } from "../portal/toolSchemas";
import { ToolResult } from "../portal/Tools";
import { PUBLIC_TOOLS, IMAGES } from "./siteData";
import { useSeo } from "./useSeo";
import { useEnquiry } from "./enquiryContext";
import PageHero from "./PageHero";
import PdfDownload from "./PdfDownload";

function ToolCards() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 site-reveal" data-testid="tools-grid">
      {PUBLIC_TOOLS.map((t) => (
        <Link key={t.id} to={`/tools/${t.id}`} className="site-card-lift group bg-white border border-slate-200 p-8 flex flex-col min-h-[260px]" data-testid={`tool-card-${t.id}`}>
          <Calculator className="h-7 w-7 text-[#C4A66A]" strokeWidth={1.5} />
          <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#C4A66A]">{t.tag}</div>
          <h3 className="font-serif text-2xl font-semibold text-[#14263D] mt-2">{t.name}</h3>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">{t.desc}</p>
          <span className="mt-auto pt-6 text-[11px] uppercase tracking-[0.25em] text-[#14263D] inline-flex items-center gap-2">Use tool <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
        </Link>
      ))}
    </div>
  );
}

export function ToolsIndex() {
  useSeo("Free CA Tools — Ind AS 116, EIR, Companies Act Applicability", "Free calculators from VCAJ & Associates LLP: Ind AS 116 lease accounting, effective interest rate, Companies Act 2013, CARO 2020 and CSR applicability.");
  return (
    <div data-testid="tools-page">
      <PageHero testid="tools-hero" image={IMAGES.tools} eyebrow="Tools" title="Free technical calculators."
        intro="The same engines our audit and advisory teams use, open to you with no sign-up. Results are indicative; talk to us before relying on them for reporting." />
      <section className="py-24 bg-[#F8FAFC]"><div className="max-w-7xl mx-auto px-6 lg:px-10"><ToolCards /></div></section>
    </div>
  );
}

function Field({ f, value, onChange }) {
  const cls = "rounded-none h-11 border-slate-300 focus-visible:ring-[#C4A66A]";
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{f.label}</span>
      <div className="mt-2">
        {f.type === "select" ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:border-[#C4A66A]" data-testid={`tool-input-${f.key}`}>
            {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : (
          <Input type={f.type === "number" ? "number" : "text"} step="any" value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`tool-input-${f.key}`} />
        )}
      </div>
    </label>
  );
}

export function ToolRun() {
  const { toolId } = useParams();
  const meta = PUBLIC_TOOLS.find((t) => t.id === toolId);
  const schema = getSchema(toolId);
  const [inputs, setInputs] = useState(() => Object.fromEntries(schema.map((f) => [f.key, f.default ?? ""])));
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const openEnquiry = useEnquiry();
  useSeo(meta ? `${meta.name} — Free Calculator` : "Tools", meta?.desc || "");
  if (!meta) return <Navigate to="/tools" replace />;

  const buildPayload = () => {
    const payload = {};
    for (const f of schema) {
      const v = inputs[f.key];
      payload[f.key] = f.type === "number" ? parseFloat(v) || 0 : f.type === "select" && (v === "true" || v === "false") ? v === "true" : v;
    }
    return payload;
  };

  const run = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    setRunning(true);
    try {
      const { data } = await api.post("/public/tools/run", { tool_id: toolId, inputs: payload });
      setResult(data.result);
      setTimeout(() => document.getElementById("tool-output")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid={`tool-page-${toolId}`}>
      <PageHero testid="tool-hero" image={IMAGES.tools} eyebrow={meta.tag} title={meta.name} intro={meta.desc} />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/tools" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-[#14263D]" data-testid="tool-back-link"><ArrowLeft className="h-3.5 w-3.5" /> All tools</Link>
          <form onSubmit={run} className="mt-8 bg-white border border-slate-200 p-8 md:p-10" data-testid="tool-form">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {schema.map((f) => <Field key={f.key} f={f} value={inputs[f.key]} onChange={(v) => setInputs({ ...inputs, [f.key]: v })} />)}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 justify-between">
              <p className="text-xs text-slate-500 max-w-md">Amounts in ₹. Indicative output for information only; not professional advice.</p>
              <button type="submit" disabled={running} className="btn-site-navy h-12 px-8 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="tool-run-btn">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Calculate
              </button>
            </div>
          </form>
          {result && (
            <div id="tool-output" className="mt-10 scroll-mt-24" data-testid="tool-output">
              <div className="flex justify-end mb-4"><PdfDownload toolId={toolId} getPayload={buildPayload} /></div>
              <ToolResult result={result} />
              <div className="mt-8 bg-[#14263D] text-white p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="font-serif text-2xl">Need this reviewed for your financial statements?</div>
                  <p className="text-white/60 text-sm mt-1">Our team can prepare the full working, journal entries and disclosures.</p>
                </div>
                <button onClick={() => openEnquiry({ service: "Accounting & Reporting" })} className="btn-site-gold h-12 px-7 text-sm font-semibold inline-flex items-center gap-2 w-fit" data-testid="tool-enquire-btn">Talk to us <ArrowUpRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
